import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

const SAFE_HREF_SCHEMES = /^(?:https?:|mailto:|tel:)/iu;
const HAS_SCHEME = /^[a-z][a-z\d+.-]*:/iu;
const PROTOCOL_RELATIVE_HREF = /^\/\//u;
const INTERNAL_HREF = /^(?:\/(?!\/)|#|\?)/u;
const DOMAIN_LIKE_HREF = /^(?:(?:www\.)|(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?\.)+[a-z]{2,})(?::\d{2,5})?(?:[/?#].*)?$/iu;

function normalizeSafeHref(href: string): string | null {
  const normalizedHref = href.trim();
  if (!normalizedHref) {
    return null;
  }

  if (SAFE_HREF_SCHEMES.test(normalizedHref)) {
    return normalizedHref;
  }

  if (PROTOCOL_RELATIVE_HREF.test(normalizedHref)) {
    const hrefWithoutSlashes = normalizedHref.slice(2);
    return DOMAIN_LIKE_HREF.test(hrefWithoutSlashes) ? `https:${normalizedHref}` : null;
  }

  if (INTERNAL_HREF.test(normalizedHref)) {
    return normalizedHref;
  }

  if (HAS_SCHEME.test(normalizedHref)) {
    return null;
  }

  // Quill stores bare domains as entered; browsers would otherwise treat them as relative paths.
  return DOMAIN_LIKE_HREF.test(normalizedHref) ? `https://${normalizedHref}` : null;
}

@Pipe({ name: 'safeHtml', standalone: false })
export class SafeHtmlPipe implements PipeTransform {
  constructor(private readonly sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    const doc = new DOMParser().parseFromString(value ?? '', 'text/html');
    doc.querySelectorAll('a').forEach((a) => {
      const href = a.getAttribute('href') ?? '';
      const safeHref = normalizeSafeHref(href);
      if (safeHref) {
        a.setAttribute('href', safeHref);
      } else {
        a.removeAttribute('href');
      }
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    });
    return this.sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  }
}
